import { Controller } from "@hotwired/stimulus"

// Custom autocomplete dropdown for text inputs.
// Replaces native <datalist> with a styled dropdown.
//
// Usage:
//   .gl-autocomplete-wrapper{ data: { controller: 'autocomplete-input' } }
//     %input{ data: { 'autocomplete-input-target': 'input', action: 'input->autocomplete-input#filter focus->autocomplete-input#open' } }
//     .gl-autocomplete-options{ data: { 'autocomplete-input-target': 'dropdown' } }
//       %button.gl-autocomplete-option{ data: { value: 'Foo', action: 'click->autocomplete-input#select' } } Foo

export default class extends Controller {
  static targets = ["input", "dropdown"]
  // When set, options are restricted to those whose `parentId` matches. Used by
  // the imo-agency controller to scope the agency list to the chosen IMO.
  static values = { parentId: String }

  connect() {
    this._outsideClick = (e) => {
      if (!this.element.contains(e.target)) this.close()
    }
    document.addEventListener("click", this._outsideClick)

    // Build options from data attribute if present
    const optionsData = this.element.dataset.options
    if (optionsData) {
      try {
        this._options = JSON.parse(optionsData)
      } catch { this._options = [] }
    } else {
      // Collect from existing option buttons
      this._options = Array.from(this.dropdownTarget.querySelectorAll('.gl-autocomplete-option')).map(el => ({
        name: el.dataset.value,
        parentId: el.dataset.parentId
      }))
    }
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
  }

  open() {
    this.filter()
  }

  close() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("open")
    }
  }

  // Scope options to a parent id (e.g. agencies under the selected IMO) and
  // re-render if the dropdown is currently open.
  setParent(parentId) {
    this.parentIdValue = parentId == null ? "" : String(parentId)
    if (this.hasDropdownTarget && this.dropdownTarget.classList.contains("open")) this.filter()
  }

  filter() {
    if (!this.hasDropdownTarget || !this.hasInputTarget) return

    const pool = this.parentIdValue
      ? this._options.filter(o => String(o.parentId) === this.parentIdValue)
      : this._options

    const query = this.inputTarget.value.trim().toLowerCase()
    const filtered = query
      ? pool.filter(o => o.name.toLowerCase().includes(query))
      : pool

    if (filtered.length === 0 || (filtered.length === 1 && filtered[0].name.toLowerCase() === query)) {
      // Clear any previously-rendered options before closing — otherwise stale
      // buttons linger in the DOM (e.g. when a parent filter newly matches
      // nothing, like an unknown IMO scoping the agency list to empty).
      this.dropdownTarget.innerHTML = ""
      this.close()
      return
    }

    this.dropdownTarget.innerHTML = filtered.slice(0, 20).map(o =>
      `<button type="button" class="gl-autocomplete-option" data-value="${this._escapeHtml(o.name)}" data-action="click->autocomplete-input#select">${this._highlightMatch(o.name, query)}</button>`
    ).join("")

    this.dropdownTarget.classList.add("open")
  }

  select(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value
    if (this.hasInputTarget) {
      this.inputTarget.value = value
      this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.close()
  }

  _highlightMatch(text, query) {
    if (!query) return this._escapeHtml(text)
    const escaped = this._escapeHtml(text)
    const idx = text.toLowerCase().indexOf(query)
    if (idx === -1) return escaped
    return escaped.substring(0, idx) +
      `<strong>${escaped.substring(idx, idx + query.length)}</strong>` +
      escaped.substring(idx + query.length)
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
